import {noop} from '@benchling/js-utils';
import {useContext} from 'react';

import {SmartChipSnapshotContext} from './SmartChipSnapshotContext';
import {SmartChipType} from './SmartChipTypes';
import {resolveFromSnapshot} from './snapshotAdapters';

interface SnapshotResolutionResult<T> {
  item: T | undefined;
  isInaccessible: boolean;
  handleClickLink: () => void;
  itemUrl: string | null;
}

/**
 * Resolves chip data from the SmartChipSnapshotContext (populated during PDF export).
 *
 * During export, the entry's chip state is pre-collected into a snapshot map so that
 * chip components can render without hitting the GQL cache (which isn't populated in
 * the headless export environment — see SnapshotContextProvider). In interactive mode
 * the context is null and this hook returns null, signalling the caller to fall through
 * to the normal GQL cache path.
 *
 * Returns null when:
 * - The snapshot context is absent (interactive mode)
 * - The id is null/undefined
 * - The id is not found in the snapshot map
 *
 * The `as T` cast is validated by the typed adapter output (e.g. StorableAdapterOutput)
 * at the adapter definition site. The cast will be removable once all chip type adapters
 * are implemented (BNCH-153158).
 */
export function useSnapshotResolution<T>(
  id: string | null | undefined,
  getUrlFn: ((item: T) => string | null) | null | undefined,
  getSmartChipTypeFromId: (id: string) => SmartChipType
): SnapshotResolutionResult<T> | null {
  const snapshotMap = useContext(SmartChipSnapshotContext);

  if (id == null || snapshotMap == null) {
    return null;
  }

  const resolved = resolveFromSnapshot(id, snapshotMap, getSmartChipTypeFromId);
  if (resolved == null) {
    return null;
  }

  return {
    // Cast is validated by StorableAdapterOutput type at adapter definition site
    item: resolved.item as T | undefined,
    isInaccessible: resolved.isInaccessible,
    handleClickLink: noop,
    itemUrl: resolved.item && getUrlFn ? getUrlFn(resolved.item as T) : null,
  };
}
